import { Check } from "lucide-react";

import { cn } from "@/lib/utils/cn";

export interface StepperStep {
  key: string;
  label: string;
  /** Override completion state; defaults to "before the current step". */
  done?: boolean;
}

interface StepperProps {
  steps: StepperStep[];
  current: string;
  onSelect: (key: string) => void;
  ariaLabel?: string;
}

/**
 * Horizontal, keyboard-accessible step switcher (a tablist, not routes). Each
 * step is a pill with a numbered dot that becomes a check once completed; the
 * current step is accented. Shared across the multi-step flows (action plans,
 * communications authoring, expert registration).
 */
export function Stepper({ steps, current, onSelect, ariaLabel }: StepperProps) {
  const currentIndex = steps.findIndex((s) => s.key === current);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className="flex flex-wrap gap-2"
    >
      {steps.map((step, index) => {
        const isCurrent = step.key === current;
        const done = step.done ?? index < currentIndex;
        return (
          <button
            key={step.key}
            role="tab"
            type="button"
            aria-selected={isCurrent}
            onClick={() => onSelect(step.key)}
            className={cn(
              "flex min-h-touch items-center gap-2.5 rounded-lg border px-3.5 py-2 text-sm font-semibold transition-colors",
              isCurrent
                ? "border-primary bg-primary/5 text-primary"
                : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "grid size-6 shrink-0 place-items-center rounded-full border text-xs font-bold tabular-nums",
                isCurrent
                  ? "border-primary bg-primary text-primary-foreground"
                  : done
                    ? "border-success bg-success text-success-foreground"
                    : "border-border text-muted-foreground",
              )}
            >
              {done && !isCurrent ? (
                <Check className="size-3.5" aria-hidden />
              ) : (
                index + 1
              )}
            </span>
            {step.label}
          </button>
        );
      })}
    </div>
  );
}
